import { makeKey, styx } from 'modules/Utils';
import { CSSProperties, useState } from 'react';
import useWebsiteBuilder, { WebsiteBuilderContent, WebsiteBuilderStyle } from 'Hooks/useWebsiteBuilder';
import { whiteOrBlackTextColor } from 'helpers/StyleUtils';

interface TabsPreviewContentProps {
    contentIndex: number;
    websiteContent: WebsiteBuilderContent[];
    websiteStyle: WebsiteBuilderStyle;
    style?: CSSProperties;
    onClickContentBlock?: (index: number) => void;
    onClickTab?: (tabId: string) => void; // NOTE: just for template preview
    activeTab?: string | null;
}

const TabsPreviewContent = ({
    contentIndex,
    websiteContent: websiteContentProp,
    websiteStyle: websiteStyleProp,
    style: styleProp,
    onClickContentBlock,
    onClickTab,
    activeTab: activeTabProp,
}: TabsPreviewContentProps) => {
    const {
        websiteContent: websiteContentState,
        websiteStyle: websiteStyleState,
        setWebsiteContent,
    } = useWebsiteBuilder();

    const websiteContent = websiteContentProp || websiteContentState;
    const websiteStyle = websiteStyleProp || websiteStyleState;
    const activeTabState = websiteContent[contentIndex].tabsContent?.activeTab;

    const activeTab = activeTabProp || activeTabState;

    const {
        fontFamily,
        textColor: OLD,
        backgroundColor: backgroundColorWebsiteStyle,
    } = websiteStyle;

    const tabsContent = websiteContent[contentIndex].tabsContent;

    const tabColor = tabsContent?.tabs.find(tab => tab.id === tabsContent?.activeTab)?.color || '#FFFFFF';
    const tabColorEnabled = tabsContent?.tabs.find(tab => tab.id === tabsContent?.activeTab)?.isColorEnabled || false;
    const isContentBackgroundColorEnabled = websiteContent[contentIndex].isContentBackgroundColorEnabled || false;

    let backgroundColor = 'transparent';

    const isContentTextColorEnabled = websiteContent[contentIndex].isContentTextColorEnabled;
    const contentTextColor = websiteContent[contentIndex].contentTextColor;

    const textColor = isContentTextColorEnabled ? contentTextColor : whiteOrBlackTextColor(backgroundColorWebsiteStyle);

    if (isContentBackgroundColorEnabled) {
        backgroundColor = websiteContent[contentIndex].contentBackgroundColor || 'transparent';
    }

    // NOTE: render()
    return (
        <div
            className="tabsPreviewContentComponent"
            style={styx(
                sty.tabsPreviewContentComponent,
                styleProp,
                {
                    backgroundColor: tabColorEnabled ? tabColor : backgroundColor,
                },
            )}
            onClick={() => onClickContentBlock?.(contentIndex)}
        >
            {tabsContent?.tabs.map((tab, index) => (
                <div
                    key={makeKey(tab.id, index)}
                    className="tabsPreviewContentTab"
                    style={styx(
                        sty.tabsPreviewContentTab,
                        tab.id === activeTab && sty.tabsPreviewContentTabActive,
                        {
                            fontFamily,
                            borderColor: textColor,
                        }
                    )}
                    onClick={() => {
                        onClickTab?.(tab.id); // NOTE: this only matters if isLocalTabLogic is true
                        const updatedContent = [...websiteContent];
                        updatedContent[contentIndex].tabsContent = {
                            ...tabsContent,
                            activeTab: tab.id
                        };
                        setWebsiteContent(updatedContent);
                    }}
                >
                    {tab.title}
                </div>
            ))}
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    tabsPreviewContentComponent: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        paddingLeft: 16,
        paddingRight: 16,
    },
    tabsPreviewContentTab: {
        display: 'flex',
        flexDirection: 'row',
        flex: 1,
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0.3,
        textAlign: 'center',
        cursor: 'pointer',
        borderBottomWidth: 2,
        borderBottomStyle: 'solid',
        padding: 16,
        transition: 'opacity 0.3s ease-in-out',
        alignSelf: 'stretch',
    },
    tabsPreviewContentTabActive: {
        opacity: 1,
    }
}

export default TabsPreviewContent;
